import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormLayout } from '@/components/layout';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector/Selector';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import type { AttendanceConfigForm } from '@/schemas/attendance/attendanceConfiguration.schema';


interface AttendanceConfigurationNotificationsFormProps {
  form: AttendanceConfigForm;
  companyControlOptions: SelectorOption[];
  recipientOptions: SelectorOption[];
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

interface AbsenceNotificationFieldsProps {
  fields: Record<string, string>;
  tooltips: Record<string, string>;
  form: AttendanceConfigForm;
  recipientOptions: SelectorOption[];
}

const AbsenceNotificationFields: React.FC<AbsenceNotificationFieldsProps> = ({
  fields,
  tooltips,
  form,
  recipientOptions,
}) => {
  const { t: translate } = useTranslation('attendance');
  const { watch, setValue, errors } = form;
  const adminEmailEnabled = watch('notifyAbsenceAdmin') || watch('automaticMode') !== '';
  const err = (msg: string | undefined) => msg ? translate(msg) : undefined;
  return (
    <>
      <Checkbox
        label={fields.notifyAbsenceEmployee}
        checked={watch('notifyAbsenceEmployee')}
        onChange={(checked) => setValue('notifyAbsenceEmployee', checked, { shouldDirty: true })}
      />
      <Checkbox
        label={fields.notifyAbsenceSupervisor}
        checked={watch('notifyAbsenceSupervisor')}
        onChange={(checked) => setValue('notifyAbsenceSupervisor', checked, { shouldDirty: true })}
      />
      <Checkbox
        label={fields.notifyNoTimeControl}
        tooltipText={tooltips.notifyNoTimeControl}
        checked={watch('notifyNoTimeControl')}
        onChange={(checked) => setValue('notifyNoTimeControl', checked, { shouldDirty: true })}
      />
      <Selector
        label={fields.markingIncidentNotification}
        tooltipText={tooltips.markingIncidentNotification}
        options={recipientOptions}
        value={watch('markingIncidentRecipient') !== null ? String(watch('markingIncidentRecipient')) : undefined}
        onChange={(value) => setValue('markingIncidentRecipient', value ? Number(value) : null, { shouldDirty: true })}
      />
      <div className="flex flex-col gap-0.5">
        <Checkbox
          label={fields.notifyAbsenceAdmin}
          checked={watch('notifyAbsenceAdmin')}
          onChange={(checked) => setValue('notifyAbsenceAdmin', checked, { shouldDirty: true })}
        />
        {errors.notifyAbsenceAdmin?.message && (
          <Text variant="small-regular" color={colors.feedback.error300}>
            {translate(errors.notifyAbsenceAdmin.message)}
          </Text>
        )}
      </div>
      <LabelInput
        label={fields.adminAbsenceEmail}
        value={watch('adminAbsenceEmail')}
        onChange={(value) => setValue('adminAbsenceEmail', value, { shouldDirty: true })}
        disabled={!adminEmailEnabled}
        color={errors.adminAbsenceEmail ? 'error' : 'default'}
        supportingText={err(errors.adminAbsenceEmail?.message)}
      />
      <div className="flex flex-col gap-0.5">
        <Checkbox
          label={fields.notifyDisconnectedDevice}
          checked={watch('notifyDisconnectedDevice')}
          onChange={(checked) => setValue('notifyDisconnectedDevice', checked, { shouldDirty: true })}
        />
      </div>
      <LabelInput
        label={fields.disconnectedDeviceEmail}
        value={watch('disconnectedDeviceEmail')}
        onChange={(value) => setValue('disconnectedDeviceEmail', value, { shouldDirty: true })}
        disabled={!watch('notifyDisconnectedDevice')}
        color={errors.disconnectedDeviceEmail ? 'error' : 'default'}
        supportingText={err(errors.disconnectedDeviceEmail?.message)}
      />
    </>
  );
};

interface DelayNotificationFieldsProps {
  fields: Record<string, string>;
  tooltips: Record<string, string>;
  form: AttendanceConfigForm;
}

const DelayNotificationFields: React.FC<DelayNotificationFieldsProps> = ({
  fields,
  tooltips,
  form,
}) => {
  const { t: translate } = useTranslation('attendance');
  const { watch, setValue, errors } = form;
  const err = (msg: string | undefined) => msg ? translate(msg) : undefined;
  return (
    <>
      <Checkbox
        label={fields.notifyEntryDelay}
        tooltipText={tooltips.notifyEntryDelay}
        checked={watch('notifyEntryDelay')}
        onChange={(checked) => setValue('notifyEntryDelay', checked, { shouldDirty: true })}
      />
      <LabelInput
        label={fields.entryDelayToleranceMinutes}
        type="number"
        value={String(watch('entryDelayToleranceMinutes') ?? '')}
        onChange={(value) => setValue('entryDelayToleranceMinutes', value === '' ? null : Number(value), { shouldDirty: true })}
        color={errors.entryDelayToleranceMinutes ? 'error' : 'default'}
        supportingText={err(errors.entryDelayToleranceMinutes?.message)}
      />
      <Checkbox
        label={fields.notifyExitDelay}
        tooltipText={tooltips.notifyExitDelay}
        checked={watch('notifyExitDelay')}
        onChange={(checked) => setValue('notifyExitDelay', checked, { shouldDirty: true })}
      />
      <LabelInput
        label={fields.exitDelayToleranceMinutes}
        type="number"
        value={String(watch('exitDelayToleranceMinutes') ?? '')}
        onChange={(value) => setValue('exitDelayToleranceMinutes', value === '' ? null : Number(value), { shouldDirty: true })}
        color={errors.exitDelayToleranceMinutes ? 'error' : 'default'}
        supportingText={err(errors.exitDelayToleranceMinutes?.message)}
      />
      <Checkbox
        label={fields.contractCompanyAbsence}
        tooltipText={tooltips.contractCompanyAbsence}
        checked={watch('contractCompanyAbsence')}
        onChange={(checked) => setValue('contractCompanyAbsence', checked, { shouldDirty: true })}
      />
      <LabelInput
        label={fields.massAttendanceEmail}
        value={watch('massAttendanceEmail')}
        onChange={(value) => setValue('massAttendanceEmail', value, { shouldDirty: true })}
        color={errors.massAttendanceEmail ? 'error' : 'default'}
        supportingText={err(errors.massAttendanceEmail?.message)}
      />
      <LabelInput
        label={fields.dtFiscalizationEmails}
        tooltipText={tooltips.dtFiscalizationEmails}
        value={watch('dtFiscalizationEmails')}
        onChange={(value) => setValue('dtFiscalizationEmails', value, { shouldDirty: true })}
        color={errors.dtFiscalizationEmails ? 'error' : 'default'}
        supportingText={err(errors.dtFiscalizationEmails?.message)}
      />
      <LabelInput
        label={fields.excessHoursEmail}
        value={watch('excessHoursEmail')}
        onChange={(value) => setValue('excessHoursEmail', value, { shouldDirty: true })}
        color={errors.excessHoursEmail ? 'error' : 'default'}
        supportingText={err(errors.excessHoursEmail?.message)}
      />
    </>
  );
};

// Componente principal

export const AttendanceConfigurationNotificationsForm: React.FC<
  AttendanceConfigurationNotificationsFormProps
> = ({
  form,
  companyControlOptions,
  recipientOptions,
}) => {
  const { t: translate } = useTranslation('attendance');
  const fields = translate('configuration.fields', { returnObjects: true }) as Record<string, string>;
  const sections = translate('configuration.sections', { returnObjects: true }) as Record<string, string>;
  const tooltips = translate('configuration.tooltips', { returnObjects: true }) as Record<string, string>;
  const { watch, setValue, errors } = form;
  const err = (msg: string | undefined) => msg ? translate(msg) : undefined;

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      {/* Sección Tolerancia */}
      <FormLayout.Section title={sections.tolerance}>
        <LabelInput
          label={fields.entryAbsenceMinutes}
          tooltipText={tooltips.entryAbsenceMinutes}
          type="number"
          value={String(watch('entryAbsenceMinutes') ?? '')}
          onChange={(value) => setValue('entryAbsenceMinutes', value === '' ? null : Number(value), { shouldDirty: true })}
          color={errors.entryAbsenceMinutes ? 'error' : 'default'}
          supportingText={err(errors.entryAbsenceMinutes?.message)}
        />
        <LabelInput
          label={fields.exitAbsenceMinutes}
          tooltipText={tooltips.exitAbsenceMinutes}
          type="number"
          value={String(watch('exitAbsenceMinutes') ?? '')}
          onChange={(value) => setValue('exitAbsenceMinutes', value === '' ? null : Number(value), { shouldDirty: true })}
          color={errors.exitAbsenceMinutes ? 'error' : 'default'}
          supportingText={err(errors.exitAbsenceMinutes?.message)}
        />
        <LabelInput
          label={fields.repeatedMarkingWait}
          type="number"
          value={String(watch('repeatedMarkingWait') ?? '')}
          onChange={(value) => setValue('repeatedMarkingWait', value === '' ? null : Number(value), { shouldDirty: true })}
          color={errors.repeatedMarkingWait ? 'error' : 'default'}
          supportingText={err(errors.repeatedMarkingWait?.message)}
        />
        <Selector
          label={fields.absenceValidationControls}
          options={companyControlOptions}
          value={watch('absenceValidationControls').map(String)}
          onChange={(value) => setValue('absenceValidationControls', (Array.isArray(value) ? value : [value]).filter(Boolean).map(Number), { shouldDirty: true })}
          multiple
        />
      </FormLayout.Section>

      {/* Sección Notificaciones */}
      <FormLayout.Section title={sections.notifications}>
        <AbsenceNotificationFields
          fields={fields}
          tooltips={tooltips}
          form={form}
          recipientOptions={recipientOptions}
        />
        <DelayNotificationFields fields={fields} tooltips={tooltips} form={form} />
      </FormLayout.Section>
    </FormLayout>
  );
};
